/**
 * Contact page.
 * Feeds the /contact page (Contact-screen) and the embedded contact CTA.
 *
 * `highlights` reuse the site's "falling blocks" reveal animation to showcase
 * what the business offers. Each item's `icon` maps to a lucide icon in Contact.js.
 *
 * `collage` prefers the live S3 gallery at runtime; this list is the seed.
 */

import { ASSETS } from "./assets-data";

// Contact-section hero image.
const CONTACT_IMAGE = ASSETS.MAESTROSHIELD_OUTDOOR_CONTACT_US_IMAGE;

const ContactData = {
    en: {
        seo: {
            title: 'Contact, Your Business',
            description:
                'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            keywords:
                'contact, your business, city name, region',
            image: '/placeholder.svg',
        },

        hero: {
            titleTop: 'CONTACT',
            titleBottom: 'US.',
            intro:
                'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        },

        image: CONTACT_IMAGE,

        form: {
            title: 'Send Us a Message',
            subtitle: "We'll get back to you soon.",
            placeholders: {
                fullName: 'Your Name',
                email: 'Email Address',
                phone: 'Phone (Optional)',
                selectService: 'What can we help with?',
                message: 'Tell us a little about yourself...',
            },
            submitLabel: 'Submit',
            sendingLabel: 'Sending...',
            leadSource: 'Website',
            consent: {
                terms: 'I agree to the Terms of Service and Privacy Policy.',
                termsLink: '/terms',
                marketing: 'I agree to receive email and SMS updates & campaigns.',
                required: 'Please accept the Terms of Service to continue.',
            },
        },

        topics: [
            { id: 'permit-closeout', title: 'Item One' },
            { id: 'violation-resolution', title: 'Item Two' },
            { id: 'stop-work-orders', title: 'Item Three' },
            { id: 'plans-engineering', title: 'Item Four' },
            { id: 'other', title: 'Something Else' },
        ],
        otherLabel: 'Other',

        social: {
            orLabel: 'OR',
            channels: [
                { type: 'instagram', label: 'Instagram', value: '@yourbusiness', href: 'https://www.example.com' },
                { type: 'whatsapp', label: 'WhatsApp', value: 'Message Us', href: 'https://www.example.com' },
                { type: 'email', label: 'Email', value: 'hello@example.com', href: 'mailto:hello@example.com' },
            ],
            footerLine: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            footerHighlight: "We'll introduce you.",
        },

        // Kept for the ContactInfoCard (business hours / response time).
        contactInfo: {
            hoursLabel: 'When We Gather',
            hours: [
                { days: 'Weekly', time: 'Item One' },
                { days: 'Fridays', time: 'Item Two' },
            ],
            responseLabel: 'Response Time',
            response: 'Within 1 business day',
            emailLabel: 'Email',
            whatsappLabel: 'WhatsApp',
        },

        // Kept for the lightweight sidebar form.
        lightweightForm: {
            title: 'Get a free quote',
            placeholders: {
                name: 'Full Name',
                phone: 'Phone Number',
                email: 'Email Address',
            },
            submitLabel: 'Get My Free Quote',
            sendingLabel: 'Sending…',
            leadSource: 'Homepage Form',
            errorMsg: 'Please provide a phone number or email.',
            modalEyebrow: 'Get in touch',
            modalPrompt: 'Leave your details and we’ll get back to you shortly.',
            subjectPrefix: 'Service inquiry',
        },
    },

    he: {
        seo: {
            title: 'Contact, Your Business',
            description:
                'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            keywords:
                'contact, your business, city name, region',
            image: '/placeholder.svg',
        },

        hero: {
            titleTop: 'CONTACT',
            titleBottom: 'US.',
            intro:
                'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        },

        image: CONTACT_IMAGE,

        form: {
            title: 'שלחו לנו הודעה',
            subtitle: 'נחזור אליכם בהקדם.',
            placeholders: {
                fullName: 'השם שלכם',
                email: 'כתובת אימייל',
                phone: 'טלפון (לא חובה)',
                selectService: 'במה נוכל לעזור?',
                message: 'ספרו לנו קצת על עצמכם...',
            },
            submitLabel: 'Submit',
            sendingLabel: 'שולח...',
            leadSource: 'Website',
            consent: {
                terms: 'אני מסכים/ה לתנאי השירות ולמדיניות הפרטיות.',
                termsLink: '/terms',
                marketing: 'אני מסכים/ה לקבל עדכונים וקמפיינים באימייל וב-SMS.',
                required: 'יש לאשר את תנאי השירות כדי להמשיך.',
            },
        },

        topics: [
            { id: 'permit-closeout', title: 'Item One' },
            { id: 'violation-resolution', title: 'Item Two' },
            { id: 'stop-work-orders', title: 'Item Three' },
            { id: 'plans-engineering', title: 'Item Four' },
            { id: 'other', title: 'משהו אחר' },
        ],
        otherLabel: 'אחר',

        social: {
            orLabel: 'או',
            channels: [
                { type: 'instagram', label: 'Instagram', value: '@yourbusiness', href: 'https://www.example.com' },
                { type: 'whatsapp', label: 'WhatsApp', value: 'שלחו הודעה', href: 'https://www.example.com' },
                { type: 'email', label: 'Email', value: 'hello@example.com', href: 'mailto:hello@example.com' },
            ],
            footerLine: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            footerHighlight: 'נחבר אתכם.',
        },

        contactInfo: {
            hoursLabel: 'מתי נפגשים',
            hours: [
                { days: 'שבועי', time: 'Item One' },
                { days: 'שישי', time: 'Item Two' },
            ],
            responseLabel: 'זמן תגובה',
            response: 'תוך יום עסקים אחד',
            emailLabel: 'מייל',
            whatsappLabel: 'WhatsApp',
        },

        lightweightForm: {
            title: 'Get a free quote',
            placeholders: {
                name: 'Full Name',
                phone: 'Phone Number',
                email: 'Email Address',
            },
            submitLabel: 'Get My Free Quote',
            sendingLabel: 'Sending…',
            leadSource: 'Homepage Form',
            errorMsg: 'Please provide a phone number or email.',
            modalEyebrow: 'צרו קשר',
            modalPrompt: 'השאירו פרטים ונחזור אליכם בהקדם.',
            subjectPrefix: 'בירור שירות',
        },
    },
};

export function getContactData(language) {
    return ContactData[language] || ContactData.en;
}

export default ContactData;
